// A lead's timeline, newest first: a message sent, a call, a reply, a note, a stage change, a do-not-contact mark. Each entry says
// what happened, who did it, and when (relative, with the exact time on hover), with its channel, template and words. A quick
// composer at the top adds a note.

import type { LeadActivity } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Field, Skeleton, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { useProblem } from '../../api/use-problem';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { Section } from '../../ui';
import { channelKey, kindKey } from './labels';
import { useLeadActivities, useLogLeadActivity } from './queries';

/** A glyph for each kind of entry. Decorative: the kind is also said in words. */
const KIND_ICON: Readonly<Record<LeadActivity['kind'], string>> = {
  message: 'message',
  call: 'bolt',
  visit: 'user',
  reply: 'arrowRight',
  note: 'pencil',
  stage_change: 'refresh',
  import: 'plus',
  follow_up: 'clock',
  do_not_contact: 'stop',
  conversion: 'check',
};

/** An RFC 3339 instant in UTC as the server writes one into the text of its own entries ("Follow-up set for 2026-10-17T04:30:00.000Z"). */
const INSTANT = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/g;

export function LeadTimeline({ leadId }: { readonly leadId: string }): ReactNode {
  const { t } = useTranslation('hq');
  const list = useLeadActivities(leadId);
  const rows = list.data?.pages.flatMap((page) => page.data) ?? [];

  let body: ReactNode;
  if (list.data === undefined) {
    body = list.isError ? (
      <LoadFailure error={list.error} message={t('leads.lead.timeline.unavailable')} onRetry={() => void list.refetch()} />
    ) : (
      <div aria-busy="true" className="console-lead-timeline__skeleton">
        <Skeleton shape="text" lines={4} />
      </div>
    );
  } else if (rows.length === 0) {
    body = <p className="console-subtle">{t('leads.lead.timeline.empty')}</p>;
  } else {
    body = (
      <ol className="console-lead-timeline" aria-label={t('leads.lead.timeline.title')}>
        {rows.map((activity) => (
          <TimelineEntry key={activity.id} activity={activity} />
        ))}
      </ol>
    );
  }

  return (
    <Section title={t('leads.lead.timeline.title')}>
      <NoteComposer leadId={leadId} />
      {body}
      {list.hasNextPage ? (
        <Button className="console-hq-more" variant="ghost" busy={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
          {t('leads.lead.timeline.more')}
        </Button>
      ) : null}
    </Section>
  );
}

function NoteComposer({ leadId }: { readonly leadId: string }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const log = useLogLeadActivity();
  const [text, setText] = useState('');

  const add = () => {
    if (text.trim() === '') return;
    log.mutate(
      { leadId, body: { kind: 'note', body: text.trim() } },
      {
        onSuccess: () => setText(''),
        onError: (error) => show({ tone: 'danger', title: problems.text(error, { fallback: t('leads.lead.timeline.noteFailed') }) }),
      },
    );
  };

  return (
    <form
      className="console-lead-note"
      onSubmit={(event) => {
        event.preventDefault();
        add();
      }}
    >
      <Field label={t('leads.lead.timeline.noteLabel')}>
        {(control) => (
          <textarea
            id={control.id}
            aria-describedby={control['aria-describedby']}
            aria-invalid={control['aria-invalid']}
            className="clav-input console-hq-reason"
            rows={2}
            maxLength={2000}
            placeholder={t('leads.lead.timeline.notePlaceholder')}
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
        )}
      </Field>
      <Button type="submit" variant="secondary" size="sm" disabled={text.trim() === ''} busy={log.isPending}>
        {t('leads.lead.timeline.addNote')}
      </Button>
    </form>
  );
}

function TimelineEntry({ activity }: { readonly activity: LeadActivity }): ReactNode {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const who = activity.actorName ?? t('leads.lead.timeline.system');
  // The server's own follow-up entries carry the instant as text; show it in the viewer's time like the rest of the page. What a
  // person wrote (a note, a message) is left exactly as written.
  const text =
    activity.body !== null && activity.kind === 'follow_up'
      ? activity.body.replace(INSTANT, (instant) => (Number.isNaN(Date.parse(instant)) ? instant : format.dateTime(instant, { date: 'medium', time: 'short' })))
      : activity.body;
  return (
    <li className="console-lead-timeline__item">
      <span className="console-lead-timeline__icon" aria-hidden="true">
        <Icon name={KIND_ICON[activity.kind]} />
      </span>
      <div className="console-lead-timeline__text">
        <p className="console-lead-timeline__head">
          <strong>{t(kindKey(activity.kind))}</strong>
          {activity.firstContact ? <Badge tone="info">{t('leads.lead.timeline.firstMessage')}</Badge> : null}
          {activity.channel === null ? null : <span className="console-muted">{t('leads.lead.timeline.via', { channel: t(channelKey(activity.channel)) })}</span>}
        </p>
        {activity.fromStage !== null && activity.toStage !== null ? (
          <p className="console-lead-timeline__stages">{t('leads.lead.timeline.stageChange', { from: t(`leads.stage.${activity.fromStage}`), to: t(`leads.stage.${activity.toStage}`) })}</p>
        ) : null}
        {activity.templateName === null ? null : <p className="console-hq-secondary">{t('leads.lead.timeline.template', { name: activity.templateName })}</p>}
        {text === null ? null : <p className="console-lead-timeline__body">{text}</p>}
        <p className="console-hq-secondary">
          {t('leads.lead.timeline.by', { who })} ·{' '}
          <time dateTime={activity.occurredAt} title={format.dateTime(activity.occurredAt, { date: 'medium', time: 'short' })}>
            {format.relative(activity.occurredAt)}
          </time>
        </p>
      </div>
    </li>
  );
}
